import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { KnowledgeItem } from '@api/models/knowledge-item.model';
import { IndexResponse, ShowResponse } from '@api/models/response.model';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

export interface InstallationVariantOption {
  component: 'rail' | 'unit';
  manufacturer: string;
  product_type: string;
}

export interface KnowledgeItemPayload {
  title: string;
  type: 'file' | 'link';
  component: 'rail' | 'unit' | 'general';
  manufacturer?: string | null;
  product_type?: string | null;
  url?: string | null;
  file?: File | null;
}

@Injectable({
  providedIn: 'root',
})
export class KnowledgeItemService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/knowledge-items`;

  getVariants(): Observable<{ data: InstallationVariantOption[] }> {
    return this.http.get<{ data: InstallationVariantOption[] }>(
      `${this.apiUrl}/variants`
    );
  }

  getItems(page: number = 1): Observable<IndexResponse<KnowledgeItem>> {
    const params = new HttpParams().set('page', page);
    return this.http.get<IndexResponse<KnowledgeItem>>(this.apiUrl, { params });
  }

  create(payload: KnowledgeItemPayload): Observable<ShowResponse<KnowledgeItem>> {
    return this.http.post<ShowResponse<KnowledgeItem>>(
      this.apiUrl,
      this.toFormData(payload)
    );
  }

  update(
    id: string,
    payload: KnowledgeItemPayload
  ): Observable<ShowResponse<KnowledgeItem>> {
    return this.http.post<ShowResponse<KnowledgeItem>>(
      `${this.apiUrl}/${id}`,
      this.toFormData(payload)
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  download(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/download`, {
      responseType: 'blob',
    });
  }

  private toFormData(payload: KnowledgeItemPayload): FormData {
    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('type', payload.type);
    formData.append('component', payload.component);
    if (payload.manufacturer != null && payload.manufacturer !== '') {
      formData.append('manufacturer', payload.manufacturer);
    } else {
      formData.append('manufacturer', '');
    }
    if (payload.product_type != null && payload.product_type !== '') {
      formData.append('product_type', payload.product_type);
    } else {
      formData.append('product_type', '');
    }
    if (payload.type === 'link' && payload.url) {
      formData.append('url', payload.url);
    }
    if (payload.type === 'file' && payload.file) {
      formData.append('file', payload.file);
    }
    return formData;
  }
}
